import { useEffect } from 'react';
import promoService from 'ui/services/promo';
import parameterService from 'ui/services/parameter';
import ErrorScreen from 'ui/components/molecules/ErrorScreen';
import Layout from 'ui/components/templates/Layout';
import constants from '@/constants';
import useEntryPage from 'ui/hooks/useEntryPage';
import entryPageValues from 'ui/constants/entryPageValues';
import useIsNativeView from 'ui/hooks/useIsNativeView';
import handlePageError from 'ui/helpers/utils/handlePageError';
import getNativeRedirection from 'ui/helpers/utils/getNativeRedirection';
import PromoListingPage from 'ui/components/templates/PromoListingPage';
import Metadata from 'ui/components/atoms/Metadata';
import StructuredData from 'ui/components/atoms/StructuredData';
import generateImageResizer from 'ui/helpers/utils/generateImageResizer';
import useReadyInteractive from 'ui/hooks/useReadyInteractive';
import useViewPromo from 'ui/hooks/emarsys/promo/useViewPromo';
import useSendGOPromo from 'ui/hooks/emarsys/promo/useSendGOPromo';
import { FEATURE } from 'ui/constants/emarsys/feature';
import { PAGE_NAME_PROMO } from 'ui/constants/emarsys/promo';
import { useDispatch } from 'react-redux';
import { setPageBranch } from 'ui/store/googleTagManager/actions';

const PUBLIC_URL = process.env.NEXT_PUBLIC_URL;

const PromoListPage = (props: any) => {
  const { faqs } = props;
  const breadcrumbItems = [
    {
      name: 'Home',
      item: `${PUBLIC_URL}`,
      path: `${PUBLIC_URL}`,
    },
    {
      name: 'Promo',
    },
  ];

  const { saveEntryPage } = useEntryPage();

  const isNativeView = useIsNativeView();
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(setPageBranch('promo page'));
  }, []);
  useEffect(() => {
    if (!props?.errorCode) {
      sessionStorage.removeItem('entryPage');
      saveEntryPage(entryPageValues.PROMO_LISTING_PAGE);
    }
  }, [saveEntryPage, props?.errorCode]);

  if (props.errorCode) {
    return <ErrorScreen errorCode={props.errorCode} withLayout />;
  }

  const { interactive } = useReadyInteractive();
  useViewPromo({
    pageName: PAGE_NAME_PROMO.PROMO_LISTING_PAGE,
    pageTitle: props?.promoHero?.promoTitle,
    breadcrumbItems: breadcrumbItems,
    newParams: {
      page_category: 'semua',
      page_number: props?.page,
    },
  });

  useSendGOPromo();

  return (
    <Layout isNativeView={isNativeView} showTasia={interactive}>
      {/* Metadata page */}
      <Metadata {...props.metadata} />

      {/* Data structure for SEO */}
      <StructuredData faqs={faqs?.faqList} breadcrumbs={breadcrumbItems} />

      <PromoListingPage
        {...props}
        breadcrumbItems={breadcrumbItems}
        category="SEMUA"
        feature={FEATURE.LISTING_PROMO}
      />
    </Layout>
  );
};

export async function getServerSideProps(context) {
  const { query, res } = context;
  const page = parseInt(query.page) - 1 || 0;
  const paginationBaseUrl = process.env.NEXT_PUBLIC_BASEPATH_URL;
  const { LIMIT_PROMO_PER_PAGE: pageSize } = constants;

  res.setHeader('Cache-Control', 'public, max-age=3600');

  // Handle redirection for native view
  const { isRedirecting, redirectUrl } = getNativeRedirection(context, []);
  if (isRedirecting) {
    return {
      redirect: {
        destination: redirectUrl,
        statusCode: 301,
      },
    };
  }

  /* --------------------------- GET PROMO HERO -------------------------- */
  let promoHero = null;
  let promoHeroResult = null;

  try {
    const promoHeroPromise = promoService.getPromoHeroV2(
      {
        contentCategory: 'all',
      },
      true
    );
    const promoListPromise = promoService.getPromoList(
      {
        page,
        size: constants.LIMIT_PROMO_PER_PAGE,
      },
      true
    );
    const categoryListPromise = parameterService.getCategoryType('promo', true);

    const [promoHeroResultSettled, promoListResultSettled, categoryListResultSettled] =
      await Promise.allSettled([promoHeroPromise, promoListPromise, categoryListPromise]);
    const promoHeroResults =
      promoHeroResultSettled.status === 'fulfilled' ? promoHeroResultSettled.value : null;
    if (promoListResultSettled.status !== 'fulfilled') {
      console.log(promoListResultSettled.reason);
      return handlePageError(promoListResultSettled.reason);
    }
    if (categoryListResultSettled.status !== 'fulfilled') {
      console.log(categoryListResultSettled.reason);
      return handlePageError(categoryListResultSettled.reason);
    }
    const promoListResult =
      promoListResultSettled.status === 'fulfilled' ? promoListResultSettled.value : null;
    const categoryListResult =
      categoryListResultSettled.status === 'fulfilled' ? categoryListResultSettled.value : null;

    promoHero = promoHeroResults.data?.details[0]?.headerCms;
    promoHeroResult = promoHeroResults;
    const promoList = promoListResult?.data?.content || [];

    const faqs = {
      title: promoHeroResult?.data?.faqTitlePage || '',
      faqList: promoHeroResult?.data?.faqs || [],
    };
    const totalData = promoListResult.data.totalElements || 0;
    const totalPages = promoListResult.data.totalPages || 0;
    const categoryList = [
      {
        name: 'SEMUA',
        description: null,
        priority: 0,
        path: '',
      },
      ...categoryListResult.data.map((cat) => ({
        ...cat,
        path: `/${cat.name.split(' ').join('-').toLowerCase()}`,
      })),
    ];

    const robotsTag = promoHeroResult?.data?.seoRobotTag;

    const metadata = {
      title: promoHeroResult?.data?.seoTitleTag || null,
      description: promoHeroResult?.data?.seoMetaDescription || null,
      keywords: promoHeroResult?.data?.seoKeyword || null,
      robots: process.env.ENV === 'PRD' ? robotsTag : 'noindex, nofollow',
    };

    if (promoHero) {
      promoHero.promoTitle = promoHeroResult?.data?.titlePage || '';
      promoHero.promoDescription = promoHeroResult?.data?.description || '';
      if (promoHero.heroImageLink) {
        const isMobile = Boolean(
          context.req.headers['user-agent']?.match(
            /Android|BlackBerry|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i
          )
        );
        promoHero.heroImageLink = generateImageResizer(
          promoHero.heroImageLink,
          isMobile ? 480 : 1024,
          'webp'
        );
      }
    }

    // Info section data
    const info = {
      title: promoHeroResult?.data?.subTitlePage || '',
      desc: promoHeroResult?.data?.subDescription || '',
    };

    return {
      props: {
        promoHero,
        promoList,
        faqs,
        categoryList,
        totalData,
        page: page + 1,
        totalPages,
        metadata,
        info,
        paginationBaseUrl,
        pageSize,
      },
    };
  } catch (err) {
    console.log(err);

    if (err?.response?.data?.status === 404) {
      context.res.statusCode = 404;
    } else {
      context.res.setHeader('Retry-After', '3600');
      context.res.statusCode = 503;
    }
    return handlePageError(err);
  }
}

export default PromoListPage;
